import {StyledText} from '@/components/ui/text/style'
import {typography} from '@/theme/typography'
import {genericForwardRef} from '@/utilities/helpers'
import {HTMLAttributes, ForwardedRef} from 'react'
import {CSSProperties, DefaultTheme, SupportedHTMLElements} from 'styled-components'

export type PaletteKey = Extract<
    keyof DefaultTheme['palette'],
    'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'orange' | 'green'
>

interface DefaultTextProps extends Omit<HTMLAttributes<HTMLElement>, 'as' | 'ref'> {
    size?: keyof typeof typography
    weight?: CSSProperties['fontWeight']
    render?: Extract<SupportedHTMLElements, 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label'>
}

export type TextProps<TPaletteKey extends PaletteKey = 'neutral'> =
    | (DefaultTextProps & {
          palette: TPaletteKey
          shade: keyof DefaultTheme['palette'][TPaletteKey]
      })
    | (DefaultTextProps & {
          palette?: undefined
          shade?: undefined
      })

export const Text = genericForwardRef(
    <TPaletteKey extends PaletteKey>(
        {palette, render = 'p', shade, size, weight, children, ...rest}: TextProps<TPaletteKey>,
        ref: ForwardedRef<HTMLElement>
    ) => {
        return (
            <StyledText
                {...rest}
                ref={ref}
                as={render}
                $palette={palette}
                $shade={shade ? (shade as keyof DefaultTheme['palette'][PaletteKey]) : undefined}
                $size={size}
                $weight={weight}
            >
                {children}
            </StyledText>
        )
    }
)
